import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { FormControlLabel } from 'src/components/FormControlLabel';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { updateAppointment } from 'src/models/appointment/appointment';
import { updateScheduledAppointment } from 'src/models/scheduled-appointment/scheduled-appointment';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

import type { Appointment } from 'src/models/appointment/_types';

interface ZoomSettings {
  password: string;
  description: string;
  autoRecording: string;
  muteUponEntry: boolean;
  waitingRoom: boolean;
  joinBeforeHost: boolean;
}

interface ZoomSettingsPanelProps {
  appointment: Appointment;
  onClose: () => void;
  onAppointmentUpdate?: (appointment: Appointment) => void;
  onRefresh?: () => void;
  existingSettings?: ZoomSettings | null;
  saveToScheduledAppointment?: boolean;
}

export const ZoomSettingsPanel: FC<ZoomSettingsPanelProps> = ({
  appointment,
  onClose,
  onAppointmentUpdate,
  onRefresh,
  existingSettings,
  saveToScheduledAppointment = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.viewEventDialog.zoomSettingsPanel',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });
  const queryClient = useQueryClient();

  const RECORDING_OPTIONS = [
    { id: 'none', label: t('noRecording') },
    { id: 'local', label: t('recordLocal') },
    { id: 'cloud', label: t('recordCloud') },
  ];

  // Initialize from existing settings, service defaults, or empty defaults
  const defaultSettings: ZoomSettings = existingSettings ||
    (appointment.service as { zoom?: ZoomSettings } | null)?.zoom || {
      password: '',
      description: '',
      autoRecording: 'none',
      muteUponEntry: false,
      waitingRoom: false,
      joinBeforeHost: false,
    };

  const [settings, setSettings] = useState<ZoomSettings>(defaultSettings);

  const saveMutation = useMutation({
    mutationFn: async (zoom: ZoomSettings | null) => {
      if (saveToScheduledAppointment && appointment.scheduledAppointmentId) {
        // For recurring appointments without uniqueZoomLink, save to the scheduled appointment
        // This matches v2's saveZoom which calls .save() on zoomModel (the scheduled appointment)
        await updateScheduledAppointment({
          scheduledAppointmentId: appointment.scheduledAppointmentId,
          zoom: zoom as any,
        });
        return null;
      }
      return updateAppointment({
        id: appointment.id,
        zoom: zoom as Appointment['zoom'],
      });
    },
    onSuccess: (updatedAppointment) => {
      if (updatedAppointment) {
        onAppointmentUpdate?.({
          ...updatedAppointment,
          visits: appointment.visits,
          entries: appointment.entries,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      // Re-fetch appointment to pick up server-created zoomMeeting data (join URL, etc.)
      onRefresh?.();
      toast.success(t('updateSuccess'));
      onClose();
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('updateFailed')));
    },
  });

  const handleSave = () => {
    if (!settings.password) {
      toast.error(t('passwordRequired'));
      return;
    }
    saveMutation.mutate(settings);
  };

  const handleRemove = () => {
    saveMutation.mutate(null);
  };

  return (
    <Stack spacing={2}>
      <Typography
        variant="caption"
        sx={{ display: 'block', fontStyle: 'italic' }}
      >
        {t('ensureConnected')}
      </Typography>
      <TextField
        label={t('password')}
        value={settings.password}
        onChange={(e) => setSettings({ ...settings, password: e.target.value })}
        placeholder={t('noPassword')}
        slotProps={{ htmlInput: { maxLength: 10 } }}
        size="small"
        helperText={t('passwordHelper')}
      />
      <TextField
        label={t('agenda')}
        value={settings.description}
        onChange={(e) =>
          setSettings({ ...settings, description: e.target.value })
        }
        placeholder={t('agendaPlaceholder')}
        multiline
        rows={2}
        slotProps={{ htmlInput: { maxLength: 1999 } }}
        size="small"
      />
      <BaseSelect
        label={t('autoRecording')}
        options={RECORDING_OPTIONS}
        value={settings.autoRecording}
        onChange={(value) =>
          setSettings({ ...settings, autoRecording: value as string })
        }
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={settings.muteUponEntry}
            onChange={(e) =>
              setSettings({ ...settings, muteUponEntry: e.target.checked })
            }
          />
        }
        label={t('muteOnEntry')}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={settings.waitingRoom}
            onChange={(e) =>
              setSettings({ ...settings, waitingRoom: e.target.checked })
            }
          />
        }
        label={t('enableWaitingRoom')}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={settings.joinBeforeHost}
            onChange={(e) =>
              setSettings({ ...settings, joinBeforeHost: e.target.checked })
            }
          />
        }
        label={t('allowJoinBeforeHost')}
      />
      <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
        <Button
          variant="prominent"
          size="small"
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? t('savingSettings') : t('saveSettings')}
        </Button>
        <Button
          variant="borderless"
          size="small"
          color="error"
          onClick={handleRemove}
          disabled={saveMutation.isPending}
        >
          {t('removeZoomMeeting')}
        </Button>
        <Button variant="borderless" size="small" onClick={onClose}>
          {tCommon('cancel')}
        </Button>
      </Stack>
    </Stack>
  );
};
